/**
 * 그리드 텍스트 자유 입력 + 제안 목록(업무용 UI)
 * 제안 패널은 document.body에 fixed로 붙여 overflow에 가리지 않게 함.
 */
const PANEL_BASE_STYLE = [
  'position:fixed',
  'max-height:220px',
  'overflow-y:auto',
  'overflow-x:hidden',
  'background:#ffffff',
  'border:1px solid #d0d7de',
  'border-radius:2px',
  'box-shadow:0 4px 14px rgba(15,23,42,0.12)',
  'z-index:200000',
  'display:none',
  'font-size:13px',
  'line-height:1.5',
  'color:#1f2328',
  'font-family:inherit',
  'box-sizing:border-box',
].join(';');

const ITEM_STYLE =
  'padding:8px 12px;cursor:pointer;border-bottom:1px solid #eaeef2;user-select:none;';
const ITEM_ACTIVE = '#e8ecf0';

function dedupeSuggestions(list) {
  const seen = new Set();
  const out = [];
  (Array.isArray(list) ? list : []).forEach((item) => {
    const v = String(item ?? '').trim();
    if (!v || seen.has(v)) return;
    seen.add(v);
    out.push(v);
  });
  return out;
}

export class GridSuggestionTextCellEditor {
  init(params) {
    this.params = params;
    const maxLength = params.maxLength;
    const raw = params.value;
    const initial = raw == null ? '' : String(raw);

    const rawList =
      typeof params.getSuggestions === 'function'
        ? params.getSuggestions(params)
        : params.getSuggestions;
    this.allSuggestions = dedupeSuggestions(rawList);

    this.eGui = document.createElement('div');
    this.eGui.style.cssText =
      'position:relative;width:100%;min-width:220px;box-sizing:border-box;';

    this.eInput = document.createElement('input');
    this.eInput.type = 'text';
    this.eInput.classList.add('ag-input-field-input');
    this.eInput.style.cssText =
      'width:100%;box-sizing:border-box;font-size:13px;line-height:1.5;color:#1f2328;' +
      'padding:2px 6px;min-height:24px;';
    this.eInput.value = initial;
    if (maxLength != null && maxLength > 0) {
      this.eInput.maxLength = maxLength;
    }

    this.panel = document.createElement('div');
    this.panel.setAttribute('role', 'listbox');
    this.panel.style.cssText = PANEL_BASE_STYLE;

    this.filtered = [];
    this.highlightIndex = -1;
    this.panelVisible = false;

    this.onInput = () => {
      this.highlightIndex = -1;
      this.refreshPanel();
    };
    this.onFocus = () => this.refreshPanel();
    this.onKeyDown = (e) => this.handleKeyDown(e);
    this.onDocMouseDown = (e) => {
      const t = e.target;
      if (this.eGui.contains(t) || this.panel.contains(t)) return;
      this.hidePanel();
    };
    this.onReposition = () => {
      if (this.panelVisible) this.positionPanel();
    };

    this.eInput.addEventListener('input', this.onInput);
    this.eInput.addEventListener('focus', this.onFocus);
    this.eInput.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('mousedown', this.onDocMouseDown, true);
    window.addEventListener('scroll', this.onReposition, true);
    window.addEventListener('resize', this.onReposition);

    this.eGui.appendChild(this.eInput);
    this.refreshPanel();
  }

  ensurePanelInBody() {
    if (this.panel.parentNode !== document.body) {
      document.body.appendChild(this.panel);
    }
  }

  positionPanel() {
    if (!this.eInput.isConnected) return;
    const r = this.eInput.getBoundingClientRect();
    const gap = 2;
    this.panel.style.left = `${Math.max(4, r.left)}px`;
    this.panel.style.top = `${r.bottom + gap}px`;
    this.panel.style.width = `${Math.max(160, r.width)}px`;
  }

  filterSuggestions(query) {
    const q = String(query ?? '').trim();
    if (!q) {
      return this.allSuggestions.slice(0, 30);
    }
    const lower = q.toLowerCase();
    const starts = [];
    const includes = [];
    this.allSuggestions.forEach((s) => {
      const sl = s.toLowerCase();
      if (sl.startsWith(lower)) starts.push(s);
      else if (sl.includes(lower)) includes.push(s);
    });
    return [...starts, ...includes].slice(0, 50);
  }

  hidePanel() {
    this.panel.style.display = 'none';
    this.panelVisible = false;
    this.highlightIndex = -1;
  }

  refreshPanel() {
    const q = this.eInput.value;
    this.filtered = this.filterSuggestions(q);
    this.panel.innerHTML = '';

    if (this.filtered.length === 0) {
      this.hidePanel();
      return;
    }

    this.ensurePanelInBody();

    this.filtered.forEach((text, idx) => {
      const row = document.createElement('div');
      row.setAttribute('role', 'option');
      row.textContent = text;
      row.style.cssText = ITEM_STYLE;
      row.dataset.index = String(idx);

      row.addEventListener('mouseenter', () => {
        this.setHighlight(idx);
      });

      row.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.eInput.value = text;
        this.hidePanel();
      });

      this.panel.appendChild(row);
    });

    this.panel.style.display = 'block';
    this.panelVisible = true;
    requestAnimationFrame(() => {
      this.positionPanel();
      this.applyHighlight();
    });
  }

  setHighlight(idx) {
    this.highlightIndex = idx;
    this.applyHighlight();
  }

  applyHighlight() {
    const children = this.panel.querySelectorAll('[role="option"]');
    children.forEach((el, i) => {
      if (i === this.highlightIndex) {
        el.style.backgroundColor = ITEM_ACTIVE;
      } else {
        el.style.backgroundColor = 'transparent';
      }
    });
  }

  handleKeyDown(e) {
    if (this.panel.style.display === 'none' || this.filtered.length === 0) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        this.hidePanel();
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.highlightIndex = Math.min(
        this.filtered.length - 1,
        this.highlightIndex + 1
      );
      this.applyHighlight();
      this.scrollHighlightedIntoView();
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.highlightIndex = Math.max(0, this.highlightIndex - 1);
      this.applyHighlight();
      this.scrollHighlightedIntoView();
      return;
    }
    if (e.key === 'Enter' && this.highlightIndex >= 0) {
      e.preventDefault();
      const pick = this.filtered[this.highlightIndex];
      if (pick != null) {
        this.eInput.value = pick;
      }
      this.hidePanel();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      this.hidePanel();
    }
  }

  scrollHighlightedIntoView() {
    const children = this.panel.children;
    const el = children[this.highlightIndex];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }

  getGui() {
    return this.eGui;
  }

  getValue() {
    return this.eInput.value;
  }

  afterGuiAttached() {
    // 전체 선택(select)은 첫 키 입력·IME 조합 시 글자가 사라지거나 잘리는 현상을 유발할 수 있어 사용하지 않음
    requestAnimationFrame(() => {
      this.eInput.focus();
      this.refreshPanel();
    });
  }

  destroy() {
    this.eInput?.removeEventListener('input', this.onInput);
    this.eInput?.removeEventListener('focus', this.onFocus);
    this.eInput?.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('mousedown', this.onDocMouseDown, true);
    window.removeEventListener('scroll', this.onReposition, true);
    window.removeEventListener('resize', this.onReposition);
    this.hidePanel();
    this.panel?.remove();
    this.eGui?.remove?.();
  }

  isPopup() {
    return false;
  }
}
